/**
 * IntentClassifier — Two-tier routing classifier for the Visual Editor Agent.
 *
 * Tier 1: 9 syntactically-stable regex patterns (LOCAL_ONLY + QUICK_PROPERTY + FORM)
 *         → instant synchronous result, no LLM call
 * Tier 2: LLM classification with regex fallback (on LLM failure/timeout)
 *         → replaces 42 regex patterns with a single LLM call
 */

import {
  LOCAL_ONLY_PATTERNS,
  QUICK_PROPERTY_PATTERNS,
  FORM_PATTERNS,
} from './AgentChatController';

// ─── Tier 2 Pattern Arrays (internal to classifier) ─────────────────────────

/** Patterns for data/workflow agent queries */
const DATA_WORKFLOW_PATTERNS = [
  /\b(query|fetch|get|show|list|count|aggregate|sum|average|find)\b.*\b(data|records?|entries|rows?|documents?|schema|collection|table)\b/i,
  /\b(run|execute|trigger|start|stop|pause|resume)\b.*\b(workflow|automation|pipeline|schedule|job)\b/i,
  /\b(workflow|pipeline|automation|schedule)\b.*\b(status|history|logs?|result)\b/i,
];

/** Patterns for data-to-component binding */
const DATA_COMPONENT_PATTERNS = [
  /\b(connect|bind|link)\b.*\b(chart|table|component|gauge|graph)\b.*\b(to|with)\b.*\b(data|schema|sensor|source)\b/i,
  /\b(connect|bind|link)\b.*\b(to|with)\b.*\b(data|schema|sensor|source)\b/i,
  /\b(show|display|visualize)\b.*\b(data|sensor|readings?|metrics?|temperature|humidity|pressure|voltage|current|energy|solar|wind)\b.*\b(as|in|on)\b.*\b(chart|graph|table|gauge|meter)\b/i,
  /\b(create|build|make|generate)\b.*\b(chart|graph|gauge|visualization)\b.*\b(showing|with|of|for|from|comparing|using)\b/i,
  /\b(line|bar|area|pie|scatter|heatmap|treemap|gauge)\b\s+(chart|graph)\b.*\b(of|showing|for|with|comparing|from)\b.*\b(data|sensor|readings?|metrics?|temperature|humidity|pressure)\b/i,
  /\b(plot|graph|chart|visualize)\b.*\b(temperature|humidity|current|voltage|pressure|sensor|solar|wind|energy|readings?|metrics?)\b/i,
  /\bwhat\b.*\b(data|schema|field)\b.*\b(chart|graph|gauge)\b/i,
  /\bwhat\b.*\b(chart|graph|gauge)\b.*\b(data|schema|field)\b/i,
  /\b(connect|bind|link|show)\b.*\b(data|sensor|reading|value)\b.*\b(text|label|display|gauge)\b/i,
  /\b(show|display|use|add)\b\s+\w+(_\w+)*\s+(on|for|to)\s+(this|the)\s+(chart|graph|gauge)\b/i,
  /\balso\s+(show|add|display|include)\s+\w+.*\b(chart|graph|gauge)\b/i,
];

/** Patterns for visual editing requests */
const VISUAL_EDITING_PATTERNS = [
  /\b(build|create|make|generate|design|populate|fill)\b.*\b(page|layout|dashboard|section|card|grid|form|canvas|home)\b/i,
  /\b(empty|blank)\b.*\b(page|canvas|home)\b/i,
  /\b(add|insert|place)\b.*\b(component|section|header|footer|sidebar|card|chart|table|button|text|image|container)\b/i,
  /\b(page|layout|dashboard)\b.*\b(with|containing|that has|including)\b/i,
  /\b(arrange|reorganize|restructure|rearrange)\b.*\b(component|layout|section|card)\b/i,
  /\b(build|create)\b.*\b(kpi|stat|metric)\b.*\b(row|card|section)\b/i,
  /\b(reduce|decrease|increase|add|remove|less|more)\b.*\b(vertical\s+spacing|spacing|padding|margin|empty\s+space)\b.*\b(card|cards|kpi|metric|stat|container|section)\b/i,
  /\b(hero|landing|pricing|contact|about)\s*(page|section)\b/i,
  /\bmulti[- ]?component\b/i,
  /\b(three|four|five|six|\d+)\s+(card|column|section|chart|stat)/i,
  /\b(create|build|make|generate)\b.*\b(\d+|multiple|several|multi)\s*pages?\b/i,
  /\bmulti[- ]?page\b/i,
  /\b(app|application|site|website)\b.*\b(with|containing|that has)\b.*\bpages?\b/i,
  /\b(home|about|contact|settings|profile|login|signup|dashboard)\b.*\band\b.*\b(home|about|contact|settings|profile|login|signup|dashboard)\b.*\bpages?\b/i,
  /\b(on|for|at)\s+(the\s+)?(mobile|tablet|desktop)\b.*\b(row|column|grid|size|width|height|same|spread|fill|center|align|layout|stack|evenly)\b/i,
  /\b(want|need|should|keep|put|make)\b.*\b(same\s+row|in\s+a\s+row|single\s+row|one\s+row|on\s+one\s+row|in\s+one\s+row|same\s+column|in\s+a\s+column|in\s+a\s+grid|side\s+by\s+side)\b/i,
  /\b(change|convert|switch|turn)\b.*\b(section|container|grid|layout|row|column|outdoor|indoor)\b.*\b(to|into|so)\b.*\b(row|column|grid|one|single|two|three|four|\d+)\b/i,
  // Re-styling / style-guide application — always a visual editor command (no session required)
  /\b(re-?style|re-?design|re-?theme)\b.*\b(page|canvas|layout|dashboard|component|section|everything|this)\b/i,
  /\b(redesign|restyle)\b/i,
  /\b(apply|use|follow|based\s+on)\b.*\b(style[\s-]?guide|design[\s-]?guide|theme|brand)\b/i,
  /\b(style[\s-]?guide|design[\s-]?guide)\b/i,
];

/** Follow-up modification patterns (session context required) */
const FOLLOW_UP_PATTERNS = [
  /\b(change|update|modify|adjust)\b.*\b(color|font|style|background|border|padding|margin)\b/i,
  /\b(make it|make them|make the)\b/i,
  /\b(bigger|smaller|wider|narrower|taller|shorter|darker|lighter|bolder)\b/i,
  /\b(put\s+it\s+back|undo\s+the|revert\s+the|restore\s+the)\b/i,
  /\b(a\s+little|a\s+bit|slightly|a\s+lot|much)\s+(more|less|bigger|smaller)\b/i,
];

/** Spatial and reference-based refinement patterns (session context required) */
const SPATIAL_REFINEMENT_PATTERNS: RegExp[] = [
  /\b(move|shift|push|pull|nudge)\b.{0,30}\b(up|down|left|right|above|below|higher|lower)\b/i,
  /\b(align|center|middle|line up|lined up)\b/i,
  /\b(match|same\s+as|same\s+\w+\s+as|like\s+the|copy\s+the|identical\s+to)\b/i,
  /\b(fill|full\s+width|full\s+height|stretch|expand)\b/i,
  /\b(same\s+width|same\s+height|same\s+size|equal\s+\w+)\b/i,
  /\b(on\s+the\s+(left|right|top|bottom)|in\s+the\s+(top|bottom)\s*(left|right)?|at\s+the\s+(top|bottom))\b/i,
  /\b(more\s+space|less\s+space|tighter|looser|spread\s+out|closer\s+together)\b/i,
];

// ─── Non-Visual Intent Patterns (route to user-agent) ────────────────────────

/** Agent creation and management patterns → route to user-agent */
const AGENT_CREATION_PATTERNS = [
  /\b(create|build|make|define|deploy|configure)\b.*\b(agent|bot|assistant|monitor)\b/i,
  /\b(create|build|make|deploy|new)\b.*\b(agent|bot|assistant)\b/i,
  /\b(what|which)\s+kind\s+of\s+(agent|assistant|bot)\b/i,
  /\b(recommend|suggest)\b.*\b(agent|assistant|bot|monitor)\b/i,
  /\bbest\s+\w*\s*(agent|assistant|bot|monitor)\b/i,
  /\b(my agents?|list agents?|show agents?|agent status)\b/i,
];

/** Knowledge base actions → route to user-agent */
const KNOWLEDGE_ACTION_PATTERNS = [
  /\b(create|build|compile|write|start)\b.*\b(knowledge|knowledge base|kb)\b/i,
  /\b(create|build|compile|write)\b.*\b(article|documentation)\b.*\b(about|for|on|regarding)\b/i,
  /\b(my knowledge|list knowledge|show knowledge|knowledge base)\b/i,
];

/** Workflow actions (non-visual, management) → route to user-agent */
const WORKFLOW_ACTION_PATTERNS = [
  /\b(create|build|make|define|set up)\b.*\b(workflow|automation|pipeline|rule)\b/i,
  /\b(my workflows?|list workflows?|show workflows?|workflow status)\b/i,
];

/** Data schema and group actions → route to user-agent */
const DATA_ACTION_PATTERNS = [
  /\b(create|define|set up)\b.*\b(schema|data group|data source|data model)\b/i,
  /\b(my schemas?|list schemas?|show schemas?|data groups?)\b/i,
  /\b(store|insert|save|write|put|add|populate)\b.*\b(records?|entries|data|rows?|documents?)\b.*\b(into|in|to)\b/i,
  /\b(store|insert|save|write)\b.*\b(into|in|to)\b.*\b(schema|group|collection|table|database)\b/i,
];

/** General system queries → route to user-agent */
const SYSTEM_QUERY_PATTERNS = [
  /\b(what|how|show|list|tell me about)\b.*\b(my|our|the)\b.*\b(agents?|skills?|workflows?|schemas?|knowledge|resources?|tools?|capabilities)\b/i,
  /\b(system|platform|organization)\b.*\b(status|health|overview|summary)\b/i,
];

/** General chat (clearly not visual) → route to user-agent */
const GENERAL_CHAT_PATTERNS = [
  /^(hey|hi|hello)\b/i,
  /^what can you do\b/i,
  /^who are you\b/i,
  /\b(remind me|set a timer|note to self)\b/i,
  /\b(email|notify)\b.*\b(to|about)\b/i,
];

// ─── Types ───────────────────────────────────────────────────────────────────

export type ComplexityClass =
  | 'simple-style'
  | 'simple-component'
  | 'simple-layout'
  | 'page-generation'
  | 'data-page-generation'
  | 'multi-page'
  | 'experiment';

export interface ClassificationMetadata {
  isFollowUp: boolean;
  complexity: ComplexityClass;
  mentionedBreakpoints: string[];
  followUpAgent?: 'styles' | 'layout' | 'component';
}

export interface RoutingClassificationResult {
  route: 'visual-editor-stream' | 'data-workflow' | 'local' | 'user-agent';
  metadata: ClassificationMetadata;
  confidence: number;
  cached: boolean;
  latency: number;
  source: 'tier1-regex' | 'tier2-llm' | 'tier2-fallback';
}

export interface RoutingClassificationContext {
  hasSelectedComponent: boolean;
  hasSelectedComponentType?: string;
  agentAvailable: boolean;
  hasSessionContext: boolean;
  preferUserAgentContinuation?: boolean;
}

// ─── Complexity Classification Patterns (mirrors backend) ────────────────────

const EXPERIMENT_PATTERNS = [
  /\b(try|experiment|explore)\b.*\b(different|alternative|another|new)\b.*\b(approach|way|style|design|layout|version)\b/i,
  /\b(what if|how about|could you try)\b/i,
  /\b(compare|a\/b test)\b/i,
];

const MULTI_PAGE_PATTERNS = [
  /create\s+(\d+|multiple|several|a\s+few)\s+pages/i,
  /multi[- ]?page/i,
  /\b(\d+)[- ]?page\s+(app|application|site|website)/i,
  /pages\s+(for|including|with)\s+.*(home|about|contact|settings|dashboard)/i,
  /\b(build|create|generate|make)\b.*\band\b.*\bpages?\b/i,
];

const DATA_PATTERNS = /\b(data|schema|sensor|field\s+[a-z]|moisture|temperature|readings|metrics|kpi|revenue|sales|weight|goal|progress|tracker|from\s+\w+\s+table)\b/i;
const PAGE_PATTERNS = /\b(build|create|generate|make|populate|fill)\b.*\b(page|dashboard|view|layout|screen|canvas|home)\b/i;
const STYLE_PATTERNS = /\b(color|background|font|style|theme|dark|light|border|shadow|radius|padding|margin|spacing|opacity|brand)\b/i;
const LAYOUT_PATTERNS = /\b(column|row|grid|stack|sidebar|layout|arrange|reorder|split|reorganize)\b/i;
const RESPONSIVE_LAYOUT_PATTERNS = /(?:\b(on|for|at|in)\s+(mobile|tablet|desktop)\b.*\b(row|column|grid|same|size|width|height|fill|spread|evenly|stack|align|center)\b|\b(row|column|grid|same|size|width|height|fill|spread|evenly|stack|align|center)\b.*\b(on|for|at|in)\s+(mobile|tablet|desktop)\b)/i;
const COMPONENT_ADD_PATTERNS = /\b(add|insert|create|put)\b.*\b(button|text|image|chart|table|input|card|component)\b/i;
const LAYOUT_SPACING_EDIT_PATTERNS = /\b(reduce|decrease|increase|add|remove|less|more|tighter|looser|compact|spacious)\b.*\b(vertical\s+spacing|spacing|padding|margin|gap|empty\s+space)\b.*(?:\b(container|section|row|column|grid|wrapper|header|footer)\b|\babove\b|\bbelow\b|\bbetween\b)/i;
const EXISTING_SPACING_EDIT_PATTERNS = /\b(reduce|decrease|increase|add|remove|less|more|tighter|looser)\b.*\b(vertical\s+spacing|spacing|padding|margin|gap|empty\s+space)\b.*\b(card|cards|kpi|metric|stat|container|section|row|column|grid)\b/i;

const BREAKPOINT_PATTERN = /\b(mobile|tablet|desktop)\b/gi;

// Follow-up agent classification (mirrors backend classifyFollowUp)
const FOLLOW_UP_STYLE_PATTERN = /\b(color|background|font|style|theme|dark|light|border|shadow|radius|padding|margin|spacing|opacity|brand|bigger|smaller|darker|lighter|bolder)\b/i;
const FOLLOW_UP_LAYOUT_PATTERN = /\b(move|rearrange|reorder|swap|reorganize|column|row|grid|stack|sidebar)\b/i;

// ─── IntentClassifier ────────────────────────────────────────────────────────

export class IntentClassifier {
  private static instance: IntentClassifier | undefined;
  private cache: Map<string, RoutingClassificationResult>;
  private readonly maxCacheSize = 100;

  private constructor() {
    this.cache = new Map();
  }

  static getInstance(): IntentClassifier {
    if (!IntentClassifier.instance) {
      IntentClassifier.instance = new IntentClassifier();
    }
    return IntentClassifier.instance;
  }

  /**
   * Classify user input for routing. Returns immediately for Tier 1 matches.
   * Falls back to regex on LLM timeout/error.
   */
  async classify(input: string, context: RoutingClassificationContext): Promise<RoutingClassificationResult> {
    const startTime = performance.now();
    const trimmed = input.trim();

    // 1. Tier 1: Synchronous regex check (9 patterns)
    const tier1Result = this.checkTier1(trimmed, context);
    if (tier1Result) {
      tier1Result.latency = performance.now() - startTime;
      return tier1Result;
    }

    // 2. Check cache
    const cacheKey = this.getCacheKey(trimmed, context);
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return { ...cached, cached: true, latency: performance.now() - startTime };
    }

    // 3. Always compute regex result (for fallback and A/B comparison)
    const regexResult = this.checkTier2Regex(trimmed, context);
    regexResult.latency = performance.now() - startTime;

    // High-confidence regex matches are stable enough to avoid an unnecessary
    // LLM round-trip. This keeps obvious editor commands responsive and avoids
    // noisy classifier timeout warnings when the backend is slow.
    if (regexResult.confidence >= 0.8) {
      this.addToCache(cacheKey, regexResult);
      return regexResult;
    }

    // 4. Tier 2: LLM classification with timeout and A/B logging
    try {
      const llmResult = await this.classifyWithLLM(trimmed, context);
      llmResult.latency = performance.now() - startTime;

      // A/B logging: compare regex vs LLM — push to debug store + console
      if (regexResult.route !== llmResult.route) {
        const divergence = {
          input: trimmed.slice(0, 100),
          regexRoute: regexResult.route,
          llmRoute: llmResult.route,
          llmConfidence: llmResult.confidence,
          context,
        };
        console.info('[IntentClassifier:A/B] DIVERGENCE', divergence);

        // Push to debug store for UI visibility (lazy import to avoid circular deps)
        try {
          const { useDebugStore } = await import('@/stores/ai/debug-store');
          useDebugStore.getState().recordClassificationDivergence(divergence);
        } catch { /* debug store not available */ }
      }

      this.addToCache(cacheKey, llmResult);
      return llmResult;
    } catch (error) {
      console.warn('[IntentClassifier] LLM classification failed, falling back to regex', error);
      const fallbackResult = { ...regexResult, source: 'tier2-fallback' as const };
      this.addToCache(cacheKey, fallbackResult);
      return fallbackResult;
    }
  }

  /**
   * Synchronous-only classification for callers that can't await.
   * Checks Tier 1, cache, and regex fallback (when LLM disabled).
   * Returns null if Tier 2 LLM would be needed — caller must use async classify().
   */
  classifySync(input: string, context: RoutingClassificationContext): RoutingClassificationResult | null {
    const startTime = performance.now();
    const trimmed = input.trim();

    // Tier 1: instant regex
    const tier1 = this.checkTier1(trimmed, context);
    if (tier1) {
      tier1.latency = performance.now() - startTime;
      return tier1;
    }

    // Cache hit
    const cacheKey = this.getCacheKey(trimmed, context);
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return { ...cached, cached: true, latency: performance.now() - startTime };
    }

    // LLM classification needed — caller must use async classify()
    return null;
  }

  /**
   * Tier 1: 9 syntactically-stable patterns that never need LLM.
   * Returns null if no Tier 1 match (caller should proceed to Tier 2).
   */
  private checkTier1(input: string, context: RoutingClassificationContext): RoutingClassificationResult | null {
    if (!input) {
      return this.makeResult('local', 'tier1-regex', 1.0, {
        isFollowUp: false,
        complexity: 'page-generation',
        mentionedBreakpoints: [],
      });
    }

    // LOCAL_ONLY_PATTERNS (3): slash commands, undo/redo, ctrl+z
    if (LOCAL_ONLY_PATTERNS.some(p => p.test(input))) {
      return this.makeResult('local', 'tier1-regex', 1.0, {
        isFollowUp: false,
        complexity: 'simple-component',
        mentionedBreakpoints: [],
      });
    }

    // QUICK_PROPERTY_PATTERNS (4): only when component is selected
    if (context.hasSelectedComponent && QUICK_PROPERTY_PATTERNS.some(p => p.test(input))) {
      return this.makeResult('local', 'tier1-regex', 1.0, {
        isFollowUp: false,
        complexity: 'simple-style',
        mentionedBreakpoints: [],
      });
    }

    // FORM_PATTERNS (2): form creation always routes to visual editor
    if (FORM_PATTERNS.some(p => p.test(input))) {
      return this.makeResult('visual-editor-stream', 'tier1-regex', 1.0, {
        isFollowUp: false,
        complexity: 'page-generation',
        mentionedBreakpoints: [],
      });
    }

    return null;
  }

  /**
   * Tier 2 regex fallback: replicates current routeToStream() logic exactly,
   * plus populates ClassificationMetadata with complexity/follow-up info.
   */
  private checkTier2Regex(input: string, context: RoutingClassificationContext): RoutingClassificationResult {
    if (context.agentAvailable && LAYOUT_SPACING_EDIT_PATTERNS.test(input)) {
      return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.9, {
        isFollowUp: true,
        complexity: 'simple-layout',
        mentionedBreakpoints: this.extractBreakpoints(input),
        followUpAgent: 'layout',
      });
    }

    if (context.agentAvailable && EXISTING_SPACING_EDIT_PATTERNS.test(input)) {
      return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.88, {
        isFollowUp: true,
        complexity: 'simple-style',
        mentionedBreakpoints: this.extractBreakpoints(input),
        followUpAgent: 'styles',
      });
    }

    // Data-component binding → visual editor streaming
    if (context.agentAvailable && DATA_COMPONENT_PATTERNS.some(p => p.test(input))) {
      return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.85, {
        isFollowUp: false,
        complexity: this.classifyComplexity(input, context),
        mentionedBreakpoints: this.extractBreakpoints(input),
      });
    }

    // Pure data/workflow queries → data-workflow
    if (DATA_WORKFLOW_PATTERNS.some(p => p.test(input))) {
      return this.makeResult('data-workflow', 'tier2-fallback', 0.85, {
        isFollowUp: false,
        complexity: 'simple-component',
        mentionedBreakpoints: [],
      });
    }

    // Agent must be available for remote routing
    if (!context.agentAvailable) {
      return this.makeResult('local', 'tier2-fallback', 0.9, {
        isFollowUp: false,
        complexity: 'simple-component',
        mentionedBreakpoints: [],
      });
    }

    // Explicit continuation of a user-agent recommendation flow should stay
    // on the user-agent path even when the choice text mentions "dashboard".
    if (context.preferUserAgentContinuation && this.isUserAgentContinuation(input)) {
      return this.makeResult('user-agent', 'tier2-fallback', 0.92, {
        isFollowUp: true,
        complexity: 'simple-component',
        mentionedBreakpoints: [],
      });
    }

    // Visual editing patterns → streaming
    if (VISUAL_EDITING_PATTERNS.some(p => p.test(input))) {
      return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.8, {
        isFollowUp: false,
        complexity: this.classifyComplexity(input, context),
        mentionedBreakpoints: this.extractBreakpoints(input),
      });
    }

    // Color/background swap commands route directly to the Styles agent.
    // Must be checked before SPATIAL_REFINEMENT_PATTERNS because positional words
    // like "middle" in "switch the background colors of the top and middle rows"
    // would otherwise trigger the spatial path (confidence 0.75 -> LLM -> page-generation).
    const COLOR_SWAP_PATTERN = /\b(switch|swap|exchange|flip)\b.*\b(colou?rs?|backgrounds?|background[\s-]?colou?rs?)\b/i;
    if (context.agentAvailable && COLOR_SWAP_PATTERN.test(input)) {
      return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.9, {
        isFollowUp: true,
        complexity: 'simple-style',
        mentionedBreakpoints: this.extractBreakpoints(input),
        followUpAgent: 'styles',
      });
    }

    // Spatial refinement patterns — require session context
    if (context.hasSessionContext && SPATIAL_REFINEMENT_PATTERNS.some(p => p.test(input))) {
      const followUpAgent = this.classifyFollowUpAgent(input);
      return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.75, {
        isFollowUp: true,
        complexity: this.classifyComplexity(input, context),
        mentionedBreakpoints: this.extractBreakpoints(input),
        followUpAgent,
      });
    }

    // Follow-up modifications with session context → streaming
    if (context.hasSessionContext && FOLLOW_UP_PATTERNS.some(p => p.test(input))) {
      const followUpAgent = this.classifyFollowUpAgent(input);
      return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.75, {
        isFollowUp: true,
        complexity: followUpAgent === 'styles' ? 'simple-style'
          : followUpAgent === 'layout' ? 'simple-layout' : 'simple-component',
        mentionedBreakpoints: this.extractBreakpoints(input),
        followUpAgent,
      });
    }

    // Non-visual intent patterns → user-agent
    const nonVisualPatterns = [
      ...AGENT_CREATION_PATTERNS,
      ...KNOWLEDGE_ACTION_PATTERNS,
      ...WORKFLOW_ACTION_PATTERNS,
      ...DATA_ACTION_PATTERNS,
      ...SYSTEM_QUERY_PATTERNS,
      ...GENERAL_CHAT_PATTERNS,
    ];
    if (nonVisualPatterns.some(p => p.test(input))) {
      return this.makeResult('user-agent', 'tier2-fallback', 0.8, {
        isFollowUp: false,
        complexity: 'simple-component',
        mentionedBreakpoints: [],
      });
    }

    // Visual style/property command without explicit pattern match.
    // Always treat as a simple-style modification (not page-generation) so the orchestrator
    // routes directly to the Styles agent without running the full Layout+Component pipeline.
    if (context.agentAvailable) {
      const VISUAL_STYLE_KEYWORDS = /\b(colors?|backgrounds?|fonts?|styles?|themes?|dark|light|borders?|shadows?|radii|radius|paddings?|margins?|opacit\w+|bold|italic|size|width|height|align\w*|spacing|fills?|strokes?)\b/i;
      if (VISUAL_STYLE_KEYWORDS.test(input)) {
        return this.makeResult('visual-editor-stream', 'tier2-fallback', 0.85, {
          isFollowUp: true,
          complexity: 'simple-style',
          mentionedBreakpoints: this.extractBreakpoints(input),
          followUpAgent: 'styles',
        });
      }
    }

    // Default: local
    return this.makeResult('local', 'tier2-fallback', 0.5, {
      isFollowUp: false,
      complexity: 'page-generation',
      mentionedBreakpoints: [],
    });
  }

  /**
   * Tier 2 LLM classification via agent service endpoint.
   * Calls POST /visual-editor/classify-route with 800ms timeout.
   */
  private async classifyWithLLM(
    input: string,
    context: RoutingClassificationContext,
  ): Promise<RoutingClassificationResult> {
    const { visualEditorApi } = await import('@/services/api/agentApi');

    const response = await visualEditorApi.classifyRoute(input, {
      hasSelectedComponent: context.hasSelectedComponent,
      hasSelectedComponentType: context.hasSelectedComponentType,
      agentAvailable: context.agentAvailable,
      hasSessionContext: context.hasSessionContext,
    });

    if (!response.success || !response.route) {
      throw new Error(response.error || 'Classification failed');
    }

    const route = response.route as RoutingClassificationResult['route'];
    const validRoutes = ['visual-editor-stream', 'data-workflow', 'local', 'user-agent'];
    if (!validRoutes.includes(route)) {
      throw new Error(`Invalid route from LLM: ${route}`);
    }

    return this.makeResult(route, 'tier2-llm', response.confidence ?? 0.5, {
      isFollowUp: response.isFollowUp ?? false,
      complexity: (response.complexity as ComplexityClass) ?? 'page-generation',
      mentionedBreakpoints: response.mentionedBreakpoints ?? [],
      followUpAgent: response.followUpAgent as ClassificationMetadata['followUpAgent'] ?? undefined,
    });
  }

  // ─── Metadata helpers (mirror backend logic) ──────────────────────────────

  /**
   * Classify complexity using regex heuristics (mirrors backend classifyComplexity).
   */
  private classifyComplexity(input: string, context: RoutingClassificationContext): ComplexityClass {
    const hasSelectedComponent = context.hasSelectedComponent;

    // Experiment detection
    if (EXPERIMENT_PATTERNS.some(p => p.test(input))) return 'experiment';

    // Multi-page detection
    if (MULTI_PAGE_PATTERNS.some(p => p.test(input))) return 'multi-page';

    // Data-aware page generation
    if (DATA_PATTERNS.test(input) && PAGE_PATTERNS.test(input)) return 'data-page-generation';

    // Page generation (no data reference)
    if (PAGE_PATTERNS.test(input)) return 'page-generation';

    // Simple style change on selected component
    if (STYLE_PATTERNS.test(input) && hasSelectedComponent && !PAGE_PATTERNS.test(input)) {
      return 'simple-style';
    }

    // Existing page spacing changes that target container structure should route to layout.
    if (LAYOUT_SPACING_EDIT_PATTERNS.test(input) && !PAGE_PATTERNS.test(input)) {
      return 'simple-layout';
    }

    // Existing page spacing/padding edits should stay in the targeted edit pipeline
    // even when nothing is explicitly selected.
    if (EXISTING_SPACING_EDIT_PATTERNS.test(input) && !PAGE_PATTERNS.test(input)) {
      return 'simple-style';
    }

    // Responsive layout modification
    if (RESPONSIVE_LAYOUT_PATTERNS.test(input) && !PAGE_PATTERNS.test(input)) {
      return 'simple-layout';
    }

    // Simple layout change
    if (LAYOUT_PATTERNS.test(input) && hasSelectedComponent && !PAGE_PATTERNS.test(input)) {
      return 'simple-layout';
    }

    // Simple component add/modify
    if (COMPONENT_ADD_PATTERNS.test(input) && !PAGE_PATTERNS.test(input)) {
      return 'simple-component';
    }

    // Default to page generation
    return 'page-generation';
  }

  private isUserAgentContinuation(input: string): boolean {
    const trimmed = input.trim();
    if (!trimmed) return false;

    const isAffirmativeOnly = /^(yes|yeah|yep|sure|ok|okay|do it|sounds good|that works)\b/i.test(trimmed);
    const mentionsChoice = /(^#\d+\b|\b(option|number)\s*\d+\b|\bthe\s+(first|second|third)\s+one\b|\b(best|top)\s+one\b)/i.test(trimmed);
    const mentionsContinuation = /\b(let'?s|lets)\s+(continue|go|do)\s+with\b|\b(continue|go|proceed|move)\s+with\b|\b(pick|choose|select|use)\b/i.test(trimmed);
    const mentionsAgent = /\b(agent|assistant|bot|monitor)\b/i.test(trimmed);

    return isAffirmativeOnly || (mentionsChoice && (mentionsContinuation || mentionsAgent)) || (mentionsContinuation && mentionsAgent);
  }

  /**
   * Classify which agent should handle a follow-up (mirrors backend classifyFollowUp).
   */
  private classifyFollowUpAgent(input: string): 'styles' | 'layout' | 'component' {
    if (LAYOUT_SPACING_EDIT_PATTERNS.test(input)) return 'layout';
    if (FOLLOW_UP_STYLE_PATTERN.test(input)) return 'styles';
    if (FOLLOW_UP_LAYOUT_PATTERN.test(input)) return 'layout';
    return 'component';
  }

  /** Extract mentioned breakpoints from input. */
  private extractBreakpoints(input: string): string[] {
    const matches = input.match(BREAKPOINT_PATTERN);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.toLowerCase()))];
  }

  // ─── Cache ─────────────────────────────────────────────────────────────────

  private getCacheKey(input: string, context: RoutingClassificationContext): string {
    const normalized = input.toLowerCase().trim();
    const contextKey = `${context.hasSelectedComponentType || 'none'}|${context.hasSessionContext}|${context.agentAvailable}`;
    return `${this.simpleHash(normalized)}:${contextKey}`;
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private addToCache(key: string, result: RoutingClassificationResult): void {
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, result);
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private makeResult(
    route: RoutingClassificationResult['route'],
    source: RoutingClassificationResult['source'],
    confidence: number,
    metadata: ClassificationMetadata,
  ): RoutingClassificationResult {
    return { route, metadata, confidence, cached: false, latency: 0, source };
  }

  /** Clear cache (for testing). */
  clearCache(): void {
    this.cache.clear();
  }

  /** Get cache stats (for debugging). */
  getCacheStats() {
    return { size: this.cache.size, maxSize: this.maxCacheSize };
  }

  /** Reset singleton (for testing). */
  static resetInstance(): void {
    IntentClassifier.instance = undefined;
  }
}
